import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../context/LanguageContext';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface Case { id: string; topic: string; status: string; }
interface TimeEntry { id: string; hours: number; hourly_rate: number; date: string; billable: boolean; category: string; }
interface Invoice { id: string; total_amount: number; status: string; created_at: string; client_name: string; }
interface AnalyticsViewProps { cases: Case[]; user: any; }

const generateMockData = () => {
  const now = new Date();
  const timeEntries: TimeEntry[] = [];
  const invoices: Invoice[] = [];
  const categories = ['IP/Tech', 'Corporate', 'Litigation', 'Real Estate', 'M&A'];
  const clients = ['Fjord Shipping AS', 'Nordic Investment Group', 'GlobalCorp Inc.', 'Bergen Tech Solutions', 'TechVenture Capital'];
  
  for (let i = 0; i < 365; i++) {
    const date = new Date(now); date.setDate(date.getDate() - i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const base = isWeekend ? 2 + Math.random() * 3 : 6 + Math.random() * 4;
    timeEntries.push({ id: `te-${i}`, hours: base * 0.85, hourly_rate: 400, date: date.toISOString(), billable: true, category: categories[i % 5] });
  }
  for (let i = 0; i < 50; i++) {
    invoices.push({ id: `inv-${i}`, total_amount: 15000 + Math.floor(Math.random() * 35000), status: ['paid', 'paid', 'paid', 'sent', 'draft'][i % 5], created_at: new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000).toISOString(), client_name: clients[i % 5] });
  }
  return { timeEntries, invoices };
};

const AnalyticsViewMinimal: React.FC<AnalyticsViewProps> = ({ cases, user }) => {
  const { language } = useTranslation();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);

  useEffect(() => { const d = generateMockData(); setTimeEntries(d.timeEntries); setInvoices(d.invoices); setLoading(false); }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const days = period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 90 : 365;
    const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    const current = timeEntries.filter(e => new Date(e.date) >= start);
    const currentInv = invoices.filter(i => new Date(i.created_at) >= start);
    const billable = current.filter(e => e.billable).reduce((s, e) => s + e.hours, 0);
    const revenue = billable * 400;
    const paid = currentInv.filter(i => i.status === 'paid').reduce((s, i) => s + i.total_amount, 0);
    const outstanding = currentInv.filter(i => i.status === 'sent').reduce((s, i) => s + i.total_amount, 0);
    return { billable: Math.round(billable), revenue, paid, outstanding, cases: Math.max(cases.length, 18), util: 86 };
  }, [timeEntries, invoices, cases, period]);

  const chartData = useMemo(() => {
    const days = period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 12 : 12;
    return Array.from({ length: days }, (_, idx) => {
      const i = days - 1 - idx;
      const now = new Date();
      if (period === 'year') {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        return { label: d.toLocaleDateString('en', { month: 'short' }), value: 120 + Math.sin(i * 0.6) * 35 + Math.random() * 25, full: d.toLocaleDateString('en', { month: 'long', year: 'numeric' }) };
      } else if (period === 'quarter') {
        return { label: `W${idx + 1}`, value: 32 + Math.sin(i * 0.7) * 10 + Math.random() * 8, full: `Week ${idx + 1}` };
      } else {
        const d = new Date(now); d.setDate(d.getDate() - i);
        return { label: idx % 5 === 0 ? d.getDate().toString() : '', value: (d.getDay() === 0 || d.getDay() === 6) ? 2 + Math.random() * 3 : 5 + Math.random() * 4, full: d.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' }) };
      }
    });
  }, [period]);

  const maxVal = Math.max(...chartData.map(d => d.value));

  const clients = useMemo(() => {
    const t: Record<string, number> = {};
    invoices.forEach(i => { t[i.client_name] = (t[i.client_name] || 0) + i.total_amount; });
    const m = Math.max(...Object.values(t));
    return Object.entries(t).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([n, v]) => ({ name: n, value: v, pct: (v / m) * 100 }));
  }, [invoices]);

  const fmt = (n: number) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`;

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-5 h-5 rounded-full border-2 border-gray-200 border-t-gray-900 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="analytics-view">
      <div className="max-w-5xl mx-auto px-8 py-12">
        
        {/* Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Analytics</h1>
            <p className="text-gray-400 mt-1">Performance overview</p>
          </div>
          
          {/* Period selector */}
          <div className="flex items-center gap-1">
            {(['week', 'month', 'quarter', 'year'] as const).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  period === p 
                    ? 'bg-gray-900 text-white' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {p === 'week' ? '7D' : p === 'month' ? '30D' : p === 'quarter' ? '90D' : '1Y'}
              </button>
            ))}
          </div>
        </div>

        {/* Revenue Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">Total Revenue</p>
          <div className="flex items-baseline gap-4">
            <span className="text-6xl font-semibold text-gray-900 tracking-tight">{fmt(stats.revenue)}</span>
            <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium bg-emerald-50 px-2.5 py-1 rounded-full">
              <ArrowUpRight className="w-4 h-4" />
              12.5%
            </span>
          </div>
        </motion.div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-8 mb-12 pb-12 border-b border-gray-100">
          {[
            { label: 'Billable Hours', value: `${stats.billable}`, unit: 'hrs', change: '+8.2%', up: true },
            { label: 'Active Cases', value: `${stats.cases}`, unit: '', change: '+3', up: true },
            { label: 'Utilization', value: `${stats.util}`, unit: '%', change: '-2.1%', up: false },
            { label: 'Avg. Rate', value: '$400', unit: '/hr', change: '+5%', up: true },
          ].map((stat, i) => (
            <motion.div 
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
            >
              <p className="text-sm text-gray-400 mb-2">{stat.label}</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-gray-900">{stat.value}</span>
                <span className="text-gray-400">{stat.unit}</span>
              </div>
              <div className={`flex items-center gap-1 mt-1 text-sm ${stat.up ? 'text-emerald-600' : 'text-red-500'}`}>
                {stat.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {stat.change}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Chart Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Billable Hours</h2>
            <p className="text-sm text-gray-400">
              {period === 'week' ? 'Daily breakdown' : period === 'month' ? 'Last 30 days' : period === 'quarter' ? 'Weekly average' : 'Monthly totals'}
            </p>
          </div>
          
          <div className="h-48 flex items-end gap-[2px]">
            {chartData.map((d, i) => {
              const h = (d.value / maxVal) * 160;
              const hovered = hoveredBar === i;
              return (
                <div
                  key={i}
                  className="flex-1 flex flex-col items-center justify-end relative"
                  onMouseEnter={() => setHoveredBar(i)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  <AnimatePresence>
                    {hovered && (
                      <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="absolute bottom-full mb-2 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg z-10 whitespace-nowrap shadow-xl"
                      >
                        <span className="font-semibold">{d.value.toFixed(1)}h</span>
                        <span className="text-gray-400 ml-2">{d.full}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: h }}
                    transition={{ duration: 0.5, delay: i * 0.008 }}
                    className={`w-full rounded-[3px] cursor-pointer transition-colors duration-150 ${
                      hovered ? 'bg-gray-900' : 'bg-gray-200'
                    }`}
                  />
                  {d.label && <span className="text-[10px] text-gray-400 mt-2 font-medium">{d.label}</span>}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom Grid */}
        <div className="grid grid-cols-2 gap-8">
          
          {/* Top Clients */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Top Clients</h2>
            <div className="space-y-4">
              {clients.map((client, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-4">{i + 1}</span>
                    <span className="text-sm font-medium text-gray-900">{client.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{fmt(client.value)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Billing */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Billing</h2>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Collected</span>
                  <span className="text-sm font-semibold text-gray-900">{fmt(stats.paid)}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(stats.paid / (stats.paid + stats.outstanding)) * 100}%` }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="h-full bg-gray-900 rounded-full"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Outstanding</span>
                  <span className="text-sm font-semibold text-amber-600">{fmt(stats.outstanding)}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(stats.outstanding / (stats.paid + stats.outstanding)) * 100}%` }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="h-full bg-amber-400 rounded-full"
                  />
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default AnalyticsViewMinimal;
